<template>
  <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-4xl mx-auto h-full overflow-x-auto">
    <!-- En-tête : UUID, triid, titre, statuts -->
    <div class="mb-6 border-b pb-4 flex flex-col gap-1">
      <div class="flex items-center gap-2 text-xs text-gray-400">
        <span v-if="memo?.uuid">UUID : <span class="font-mono text-gray-600">{{ memo.uuid }}</span></span>
        <span v-if="memo?.triid" class="ml-4">Triid : <span class="font-mono text-gray-600">{{ memo.triid }}</span></span>
      </div>
      <h2 class="text-2xl font-bold text-gray-800 mt-1">{{ memo?.title || 'Sans titre' }}</h2>
      <!-- Statuts -->
      <div class="flex gap-2 mt-2">
        <span v-if="memo?.is_model" class="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700 flex items-center gap-1">
          <BookTemplateIcon class="h-3.5 w-3.5" /> Modèle
        </span>
        <span v-else class="px-2 py-1 rounded-full text-xs bg-pink-100 text-pink-700 flex items-center gap-1">
          <FileIcon class="h-3.5 w-3.5" /> Fiche
        </span>
        <span :class="memo?.is_draft ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'" class="px-2 py-1 rounded-full text-xs flex items-center gap-1">
          <FileEditIcon v-if="memo?.is_draft" class="h-3.5 w-3.5" />
          <FileCheckIcon v-else class="h-3.5 w-3.5" />
          {{ memo?.is_draft ? 'Brouillon' : 'Validé' }}
        </span>
        <span :class="memo?.is_public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'" class="px-2 py-1 rounded-full text-xs flex items-center gap-1">
          <EyeIcon v-if="memo?.is_public" class="h-3.5 w-3.5" />
          <EyeOffIcon v-else class="h-3.5 w-3.5" />
          {{ memo?.is_public ? 'Public' : 'Privé' }}
        </span>
      </div>
    </div>

    <!-- Aperçu des 7W -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-full">
      <!-- WHAT (masqué si modèle) -->
      <section v-if="!memo?.is_model" class="w-full max-w-full">
        <h3 class="text-lg font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <BoxIcon class="h-5 w-5" /> WHAT
        </h3>
        <div class="flex flex-col gap-3">
          <MaterialEntityCard :material-entity="memo?.materialEntity" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
          <DigitalEntityCard v-if="showDigitalEntityInput" :digital-entity="memo?.digitalEntityInput" :is-input="true" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
          <DigitalEntityCard :digital-entity="memo?.digitalEntityOutput" :is-input="false" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
        </div>
      </section>
      <!-- WHY -->
      <section class="w-full max-w-full">
        <h3 class="text-lg font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <HelpCircleIcon class="h-5 w-5" /> WHY
        </h3>
        <ProjectCard :project="memo?.project" :instrumentation-description="memo?.instrumentation_description || ''" :instrumentation-link="memo?.instrumentation_link || ''" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
      </section>
      <!-- WHO -->
      <section class="w-full max-w-full">
        <h3 class="text-lg font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <UsersIcon class="h-5 w-5" /> WHO
        </h3>
        <div class="space-y-2 w-full max-w-full">
          <template v-if="memo?.selectedAgents && memo.selectedAgents.length">
            <AgentCard v-for="agent in memo.selectedAgents" :key="agent.uuid" :agent="agent" :roles="agent.rolesList" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
          </template>
          <AgentCard v-else :agent="null" :roles="''" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
        </div>
      </section>
      <!-- WHERE -->
      <section class="w-full max-w-full">
        <h3 class="text-lg font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <MapPinIcon class="h-5 w-5" /> WHERE
        </h3>
        <LocationCard :location="memo?.location" :view-mode="'memostable'" :show-actions="false" class="w-full max-w-full" />
      </section>
      <!-- WHEN -->
      <section class="w-full max-w-full">
        <h3 class="text-lg font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <ClockIcon class="h-5 w-5" /> WHEN
        </h3>
        <div class="bg-gray-50 rounded p-3 w-full max-w-full">
          <div v-if="memo?.start_time" class="mb-1">
            <span class="font-medium">Début :</span> {{ formatDateTime(memo.start_time) }}
          </div>
          <div v-if="memo?.end_time" class="mb-1">
            <span class="font-medium">Fin :</span> {{ formatDateTime(memo.end_time) }}
          </div>
          <div v-if="memo?.time_zone" class="text-xs text-gray-500">{{ memo.time_zone }}</div>
          <div v-if="!memo?.start_time" class="text-gray-400 text-sm">Aucune date définie</div>
        </div>
      </section>
      <!-- HOW | WHICH -->
      <section class="md:col-span-2 w-full max-w-full">
        <h3 class="text-lg font-semibold text-blue-700 mb-2 flex items-center gap-2">
          <WrenchIcon class="h-5 w-5" /> HOW | WHICH
        </h3>
        <div class="bg-gray-50 rounded p-3 w-full max-w-full">
          <template v-if="groupedDataEntries && Object.keys(groupedDataEntries).length">
            <div class="space-y-4">
              <template v-for="(group, section) in groupedDataEntries" :key="section">
                <div>
                  <h4 class="text-sm font-medium text-gray-500 mb-1">{{ section }}</h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2 w-full max-w-full">
                    <div v-for="entry in group" :key="entry.name" class="flex items-start w-full max-w-full">
                      <span class="text-sm text-gray-900">{{ entry.name?.split('.').pop() || '' }}:</span>
                      <span class="ml-2 text-sm text-gray-600">{{ entry.value }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
          <div v-else class="text-gray-400 text-sm">Aucune donnée disponible</div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { BookTemplate as BookTemplateIcon, Box as BoxIcon, Clock as ClockIcon, Eye as EyeIcon, EyeOff as EyeOffIcon, FileCheck as FileCheckIcon, FileEdit as FileEditIcon, File as FileIcon, HelpCircle as HelpCircleIcon, MapPin as MapPinIcon, Users as UsersIcon, Wrench as WrenchIcon } from 'lucide-vue-next';
import { computed } from 'vue';
import AgentCard from '../cards/AgentCard.vue';
import DigitalEntityCard from '../cards/DigitalEntityCard.vue';
import LocationCard from '../cards/LocationCard.vue';
import MaterialEntityCard from '../cards/MaterialEntityCard.vue';
import ProjectCard from '../cards/ProjectCard.vue';

const props = defineProps<{ modelValue: any, readOnlyConfig?: any }>();
const memo = computed(() => props.modelValue);

const showDigitalEntityInput = computed(() => {
  return memo.value?.selectedSchema?.type?.toLowerCase() !== 'acquisition';
});

const groupedDataEntries = computed(() => {
  if (!memo.value?.data_entries) return {};
  return memo.value.data_entries.reduce((acc: Record<string, any[]>, entry: any) => {
    const section = entry.name?.split('.')[0] || 'other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(entry);
    return acc;
  }, {});
});

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}
</script> 